import React, { forwardRef, useContext, useImperativeHandle, useState } from "react";

import { AppDrawer, AppDrawerProps } from "Components/Generic/AppDrawer";
import { useTranslation } from "react-i18next";
import {
  CheckboxesEvidenceContainer,
  EvidenceCheckbox,
  EvidenceText,
  RequireEvidenceContainer,
  WrapperFooter,
} from "./styles";
import { Button } from "Components/Form";
import { Color } from "Components/Form/Button";
import { QuestionLogics, Questions } from "Models/checklist";
import { ChecklistContext } from "../../../context";
import { QuestionTypes } from "../../../../utils";
import { Status } from "Util/enums";

export interface EvidencesDrawerHandles {
  open: (questionLogic: QuestionLogics) => void;
  close: () => void;
}

export const EvidencesDrawer = forwardRef<EvidencesDrawerHandles>((_props, ref) => {
  const { handleUpdateMultipleFieldsInQuestionLogic } = useContext(ChecklistContext);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [logic, setLogic] = useState<QuestionLogics>({} as QuestionLogics);
  const [evidenceNotesRequired, setEvidenceNotesRequired] = useState<boolean>(false);
  const [evidencePhotosRequired, setEvidencePhotosRequired] = useState<boolean>(false);

  const { t: translate } = useTranslation();

  const handleOpen = (questionLogic: QuestionLogics) => {
    setLogic(questionLogic);
    setEvidenceNotesRequired(!!questionLogic.isEvidenceNotesRequired);
    setEvidencePhotosRequired(!!questionLogic.isEvidencePhotosRequired);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const generateEvidenceQuestions = (): Questions[] => {
    const questions: Questions[] = [];

    if (evidenceNotesRequired) {
      questions.push({
        title: translate("addANoteAsEvidence"),
        description: "",
        type: QuestionTypes.TEXT,
        mandatory: true,
        needAction: false,
        questionOrder: 1,
        status: Status.ACTIVE,
        answerAction: "",
        temporaryId: Math.random().toString(36).substring(2, 15),
        questionLogics: [],
        hasDescription: false,
        isEvidence: true,
        ...(logic.id ? { logicId: logic.id } : {}),
      });
    }

    if (evidencePhotosRequired) {
      questions.push({
        title: translate("addAPhotoAsEvidence"),
        description: "",
        type: QuestionTypes.PHOTO,
        mandatory: true,
        needAction: false,
        questionOrder: 1,
        status: Status.ACTIVE,
        answerAction: "",
        temporaryId: Math.random().toString(36).substring(2, 15),
        questionLogics: [],
        hasDescription: false,
        isEvidence: true,
        ...(logic.id ? { logicId: logic.id } : {}),
      });
    }

    return questions;
  };

  const mergeEvidenceQuestions = (): Questions[] => {
    const existingQuestions = logic.questions || [];
    const newEvidenceQuestions = generateEvidenceQuestions();

    const nonEvidenceQuestions = existingQuestions.filter((question) => !question.isEvidence);

    return [...newEvidenceQuestions, ...nonEvidenceQuestions]?.map((question, index) => ({
      ...question,
      questionOrder: index + 1,
    }));
  };

  const drawerContent = () => {
    return (
      <RequireEvidenceContainer>
        <EvidenceText>
          {translate("chooseTheEvidenceThatllBeRequiredWhenThisAnswerIsSelected")}
        </EvidenceText>
        <CheckboxesEvidenceContainer>
          <EvidenceCheckbox
            checked={evidenceNotesRequired}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setEvidenceNotesRequired(event.target.checked)
            }
            label={translate("notes")}
          />
          <EvidenceCheckbox
            checked={evidencePhotosRequired}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setEvidencePhotosRequired(event.target.checked)
            }
            label={translate("photos")}
          />
        </CheckboxesEvidenceContainer>
      </RequireEvidenceContainer>
    );
  };

  const drawerSections: AppDrawerProps["sectionItems"] = [
    {
      id: "requireEvidence",
      content: drawerContent(),
    },
  ];

  const handleSave = () => {
    const updatedQuestions = mergeEvidenceQuestions();

    handleUpdateMultipleFieldsInQuestionLogic({
      fields: {
        isEvidenceNotesRequired: evidenceNotesRequired,
        isEvidencePhotosRequired: evidencePhotosRequired,
        questions: updatedQuestions,
      },
      logicTemporaryId: logic.temporaryId,
      questionLogicId: logic.id,
    });
    handleClose();
  };

  return (
    <AppDrawer
      DrawerProps={{
        open: isOpen,
        onClose: handleClose,
      }}
      title={translate("requireEvidence")}
      maxWidth="450px"
      hideSideMenu
      footer={
        <WrapperFooter>
          <Button onClick={handleClose}>{translate("cancel")}</Button>
          <Button customColor={Color.BRAND} onClick={() => handleSave()}>
            {translate("save")}
          </Button>
        </WrapperFooter>
      }
      sectionItems={drawerSections}
    />
  );
});
